import type { ReactNode } from "react";
import { Button, Heading, Pill, Text } from "../primitives";
import { CalendarIcon } from "../icons";
import styles from "./SectionHeader.module.css";

export type SectionHeaderLayout = "split" | "center";

export type SectionHeaderProps = {
  /** Étiquette au-dessus du titre. */
  badge?: ReactNode;
  /** Titre de section, UN mot ou groupe surligné via <mark>. */
  title: ReactNode;
  titleAs?: "h1" | "h2" | "h3";
  /** Texte descriptif : en face du titre en `split`, sous le titre en `center`. */
  intro?: ReactNode;
  /**
   * Paragraphe sous le titre, quand une section a une démonstration à faire et
   * pas seulement une accroche à poser. Dès qu'il est là, l'en-tête passe à
   * TROIS rangées : l'étiquette, puis le titre en face du bouton, puis les deux
   * paragraphes côte à côte (`titleText` à gauche, `intro` à droite). Le bouton
   * remonte donc en face du titre, où l'accroche se tenait.
   */
  titleText?: ReactNode;
  /**
   * `text` (défaut) : l'accroche en corps de texte, alignée à gauche de sa
   * colonne. `lead` : l'exception pour une accroche de quelques mots qui vaut
   * promesse (« On vous accompagne. ») : corps de titre h4, encre, alignée à
   * droite sur le bord du bouton. Sous 1024 px elle revient à gauche.
   */
  introVariant?: "text" | "lead";
  /** Bouton : sous l'accroche en `split`, sous le titre en `center`. */
  cta?: { label: ReactNode; href: string; icon?: "calendar" | ReactNode; arrow?: boolean };
  /** Contenu libre sous l'accroche (et sous le bouton), calé à droite comme lui : des flèches de navigation, par exemple. */
  after?: ReactNode;
  layout?: SectionHeaderLayout;
  className?: string;
};

function ctaIcon(icon: "calendar" | ReactNode | undefined): ReactNode {
  if (icon === "calendar") return <CalendarIcon />;
  return icon ?? null;
}

/**
 * L'en-tête de section de la charte : étiquette et titre EMPILÉS À GAUCHE, et
 * en face à droite le texte descriptif, le bouton, ou les deux (le bouton sous
 * le texte). C'est le gabarit par défaut de toute section ; `layout="center"`
 * est l'exception, réservée aux blocs qui n'ont rien à mettre en face.
 *
 * Sans `intro` ni `cta` le titre occupe toute la largeur : la colonne de droite
 * n'est pas créée, plutôt que laissée vide.
 *
 * `titleText` ajoute un paragraphe SOUS le titre, à gauche, pour les sections
 * qui ont une démonstration à faire et pas seulement une accroche à poser.
 */
export function SectionHeader({
  badge,
  title,
  titleAs = "h2",
  intro,
  titleText,
  introVariant = "text",
  cta,
  after,
  layout = "split",
  className,
}: SectionHeaderProps) {
  const split = layout === "split";
  const twoColumnText = split && Boolean(titleText);
  const aside = Boolean((twoColumnText ? false : intro) || cta || after);
  return (
    <div
      className={[styles.header, className].filter(Boolean).join(" ")}
      data-layout={layout}
      data-aside={split && aside ? "true" : undefined}
      data-cta={cta ? "true" : undefined}
      data-title-text={split && titleText ? "true" : undefined}
    >
      {/* L'étiquette occupe sa propre rangée, sur toute la largeur : dans la
          colonne du titre, elle décalerait le haut de la colonne de gauche et
          il deviendrait impossible d'aligner le titre et l'accroche par le
          haut sans les aligner sur la pastille. */}
      {badge ? <Pill className={styles.badge}>{badge}</Pill> : null}
      <div className={styles.headline}>
        <Heading as={titleAs} size="h2" align={split ? "left" : "center"} className={styles.title}>
          {title}
        </Heading>
      </div>
      {split && aside ? (
        <div className={styles.aside} data-intro={!twoColumnText && intro ? introVariant : undefined}>
          {/* En trois rangées, l'accroche descend d'une rangée : ne reste ici
              que ce qui doit se tenir en face du titre. */}
          {!twoColumnText && intro ? (
            <Text className={[styles.intro, introVariant === "lead" ? styles.introLead : ""].filter(Boolean).join(" ")}>
              {intro}
            </Text>
          ) : null}
          {cta ? (
            <Button className={styles.action} href={cta.href} icon={ctaIcon(cta.icon)} arrow={cta.arrow}>
              {cta.label}
            </Button>
          ) : null}
          {after ? <div className={styles.after}>{after}</div> : null}
        </div>
      ) : null}
      {twoColumnText ? (
        <div className={styles.texts}>
          <Text className={styles.intro}>{titleText}</Text>
          {intro ? <Text className={styles.intro}>{intro}</Text> : null}
        </div>
      ) : null}
      {!split && intro ? (
        <Text className={styles.intro} align="center">
          {intro}
        </Text>
      ) : null}
      {!split && cta ? (
        <Button href={cta.href} icon={ctaIcon(cta.icon)} arrow={cta.arrow}>
          {cta.label}
        </Button>
      ) : null}
    </div>
  );
}
